'use client'

import { useT, type TranslateFn } from '@/lib/i18n'

/**
 * «Детали исторической связки» — одна строка таблицы примеров, раскрытая целиком.
 *
 * ЧТО ЭТО ЗА ОБЪЕКТ. Строка исторического набора сценариев 1–5 — это СВЯЗКА
 * «импорт → экспорт»: одна импортная запись корпуса и одна экспортная запись корпуса,
 * которые историческая логика сопоставления соединила в пару. Это НЕ подтверждённая
 * физическая перевозка, НЕ доказанное продолжение одного и того же груза и НЕ кейс
 * реестра «Кейсы» — это наблюдение исторического фона, на котором измеряется
 * необычность ТЕКУЩЕГО кейса. Подпись живёт в словаре модуля
 * (`analytics.hist.detailTitle`).
 *
 * ЗАЧЕМ ОНА ЕСТЬ
 * Таблица примеров остаётся компактным обзором: в строку помещается десять полей, и
 * этого хватает, чтобы увидеть закономерность. Карточка отвечает на другой вопрос —
 * «что конкретно происходило в этой перевозке» — и поэтому НЕ является вертикальной
 * копией строки. В ней есть то, чего в строке нет и не будет:
 *   • НАЗВАНИЯ плательщиков рядом с их кодами;
 *   • названия и БИН отправителя и получателя каждой ноги;
 *   • внутренние станции: строка показывает Томск-Грузовой → Новоблаговещенка, а
 *     перевозка на самом деле шла Томск-Грузовой → Павлодар-Южный, потом
 *     Павлодар-Южный → Новоблаговещенка;
 *   • страны каждой ноги по отдельности;
 *   • пограничные переходы;
 *   • точное время приёма, отправления и раскредитования.
 *
 * ЧТО ОНА НЕ ДЕЛАЕТ
 * Не считает, не сравнивает коды со справочниками и не заполняет пустую сторону
 * значением соседней. Блок «Что изменилось» показывает ФАКТ различия и называет его
 * нейтрально — «изменилось» / «совпадает» / «нет данных», — а не нарушением: такого
 * вывода в канонических данных нет, и делать его на фронте нельзя.
 *
 * ЗАГОЛОВОК именно «историческая связка», а не «перевозка» и не «кейс»: пара записей
 * корпуса не обязана быть ни подтверждённой перевозкой, ни зарегистрированным кейсом
 * платформы, и приписывать ей чужой `case_id` неверно.
 */
import { useEffect, useState } from 'react'
import { useCaseHistoryDetailV3 } from '../anomalies-v3.hooks'
import {
  etsngCodesEqual,
  etsngComparisonRows,
  etsngRowsFor,
} from '@/lib/etsng-classification'
import type {
  HistoryDetailContext,
  HistoryLegDetail,
  HistoryPairDetail,
} from '../history-detail.types'

/** Точная идентичность обеих ног — то, чем открывается карточка. */
export interface HistoryRowIdentity {
  importRecordId: string
  exportRecordId: string
  /** Только для подписи кнопки и aria-label; ключом запроса не является. */
  importShipment: string | null
  exportShipment: string | null
}

/* ── примитивы ───────────────────────────────────────────────────────────────── */

const DASH = <span className="text-[var(--text-tertiary)]">—</span>

/** Подпись → значение. Пустое значение печатается прочерком, а не прячется: */
/*  отсутствие данных — это тоже факт, и аналитик должен его видеть.            */
function Row({ k, v, mono, title }: {
  k: string; v: string | null | undefined; mono?: boolean; title?: string
}) {
  return (
    <div className="grid grid-cols-[minmax(0,150px)_1fr] items-baseline gap-2 py-px">
      <dt className="text-[11px] leading-tight text-[var(--text-tertiary)]">{k}</dt>
      <dd
        className={`min-w-0 break-words text-[12px] leading-tight text-[var(--text-secondary)] ${mono ? 'font-mono tabular-nums' : ''}`}
        title={title ?? (v || undefined)}
      >
        {v || DASH}
      </dd>
    </div>
  )
}

function Block({ title, children, tone }: {
  title: string; children: React.ReactNode; tone?: 'import' | 'export'
}) {
  const t = useT()
  const accent = tone === 'import'
    ? 'border-l-2 border-l-[var(--accent-data)]'
    : tone === 'export'
      ? 'border-l-2 border-l-[var(--text-tertiary)]'
      : ''
  return (
    <section className={`rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3 ${accent}`}>
      <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)]">
        {title}
      </h4>
      <dl>{children}</dl>
    </section>
  )
}

/** «66.460 т» — масса как есть; отсутствующая масса НЕ становится нулём. */
function weight(v: number | null): string | null {
  return typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(3)} т` : null
}

/** «04.07.2025» из ISO-даты. */
function ru(iso: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''))
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null
}

/** «02.07.2025 08:33:15» — точное время не теряется, если источник его несёт. */
function ruTime(ts: string | null): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}:\d{2})/.exec(String(ts ?? ''))
  if (m) return `${m[3]}.${m[2]}.${m[1]} ${m[4]}`
  return ru(ts)
}

function days(n: number | null, t: TranslateFn): string | null {
  if (n == null) return null
  // Формы множественного числа даёт t() через Intl.PluralRules — ручная
  // русская логика (день/дня/дней) для казахского неверна.
  return t('analytics.hist.days', { count: Math.abs(Math.trunc(n)) })
}

/* ── нога ────────────────────────────────────────────────────────────────────── */

function LegSection({ leg, title }: { leg: HistoryLegDetail; title: string }) {
  const t = useT()
  const unitLabel = leg.unitKind === 'container' ? t('analytics.hist.container') : t('common.field.wagon')
  return (
    <Block title={title} tone={leg.side}>
      <Row k={t('common.field.date')} v={ru(leg.date)} mono />
      <Row k={t('analytics.leg.shipmentNo')} v={leg.shipmentId} mono />
      <Row k={unitLabel} v={leg.unit} mono />
      <Row k={t('common.field.countryFrom')} v={leg.countryFrom} />
      <Row k={t('common.field.countryTo')} v={leg.countryTo} />
      <Row k={t('analytics.leg.stationFrom')} v={leg.stationFrom} />
      <Row k={t('analytics.leg.stationTo')} v={leg.stationTo} />
      <Row k={t('analytics.leg.border')} v={leg.borderIn ?? leg.borderOut} />
      <Row k={t('common.field.cargo')} v={leg.cargoName} />
      <Row k={t('analytics.leg.etsng')} v={leg.etsng} mono />
      {/* Классификация ЕТСНГ этой ноги — из общего замороженного справочника.
          Стоит сразу под кодом, которому принадлежит, и до ГНГ: это уровни ТОГО ЖЕ
          классификатора, а ГНГ — другой. Кода нет или справочник его не знает —
          строк нет вовсе, отображение самого кода при этом не меняется.
          Подписи уровней приходят из справочника — они данные, а не интерфейс. */}
      {etsngRowsFor(leg.etsng).map((r) => (
        <Row key={r.key} k={r.label} v={r.value} />
      ))}
      <Row k={t('analytics.leg.gng')} v={leg.gng} mono />
      <Row k={t('analytics.leg.mass')} v={weight(leg.weightTons)} mono />
      {/* Названия участников и плательщика приходят точными или не приходят вовсе. */}
      <Row k={t('analytics.leg.sender')} v={leg.senderName} />
      <Row k={t('analytics.leg.senderBin')} v={leg.senderBin} mono />
      <Row k={t('analytics.leg.receiver')} v={leg.receiverName} />
      <Row k={t('analytics.leg.receiverBin')} v={leg.receiverBin} mono />
      <Row k={t('analytics.leg.payer')} v={leg.payerName} />
      <Row k={t('analytics.leg.payerCode')} v={leg.payerCode} mono />
      {/* Время операций — то, чего в строке таблицы нет вовсе. */}
      <Row k={t('analytics.leg.departure')} v={ruTime(leg.departureAt)} mono />
      <Row k={t('analytics.leg.cargoAccepted')} v={ruTime(leg.acceptAt)} mono />
      <Row k={t('analytics.leg.cleared')} v={ruTime(leg.creditAt)} mono />
    </Block>
  )
}

/* ── маршрут и стыковка ──────────────────────────────────────────────────────── */

/**
 * Маршрут целиком, включая внутреннее плечо, которого в строке таблицы не видно.
 *
 * Ни одна промежуточная точка не достраивается: печатается ровно то, что несут обе
 * ноги. Отсутствующее звено остаётся прочерком.
 */
function RouteBlock({ p }: { p: HistoryPairDetail }) {
  const t = useT()
  const steps: [string, string | null][] = [
    [t('analytics.route.importCountryFrom'), p.import.countryFrom],
    [t('analytics.route.importStationFrom'), p.import.stationFrom],
    [t('analytics.route.importStationTo'), p.import.stationTo],
    [t('analytics.route.exportStationFrom'), p.export.stationFrom],
    [t('analytics.route.exportStationTo'), p.export.stationTo],
    [t('analytics.route.exportCountryTo'), p.export.countryTo],
  ]
  return (
    <Block title={t('common.field.route')}>
      <div className="mb-2 flex flex-col gap-0.5">
        {steps.map(([label, v], i) => (
          <div key={label} className="flex items-baseline gap-2">
            <span className="w-3 shrink-0 text-center text-[10px] text-[var(--text-tertiary)]">
              {i === 0 ? '' : '↓'}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--text-primary)]" title={v ?? undefined}>
              {v || '—'}
            </span>
            <span className="shrink-0 text-[10px] text-[var(--text-tertiary)]">{label}</span>
          </div>
        ))}
      </div>
    </Block>
  )
}

/**
 * Внутренняя стыковка: сравнение двух станций — и ничего сверх него.
 *
 * «Совпадают» здесь означает буквальное равенство названий, а не вывод о законности
 * перегруза. Когда одной из станций нет, вывод не делается вовсе.
 */
function HandoverBlock({ p }: { p: HistoryPairDetail }) {
  const t = useT()
  const a = p.import.stationTo
  const b = p.export.stationFrom
  const verdict = a && b ? (a === b ? t('analytics.value.yesLower') : t('analytics.value.noLower')) : null
  return (
    <Block title={t('analytics.handover.title')}>
      <Row k={t('analytics.route.importStationTo')} v={a} />
      <Row k={t('analytics.route.exportStationFrom')} v={b} />
      <Row k={t('analytics.handover.matches')} v={verdict} />
      <Row k={t('analytics.handover.intervalBetweenLegs')} v={days(p.daysBetween, t)} />
    </Block>
  )
}

/* ── что изменилось ──────────────────────────────────────────────────────────── */

/** Нейтральная метка различия. Никакой квалификации — только факт. */
type DiffState = 'same' | 'changed' | 'noData'

/** Ключи подписей состояний — отображение отделено от самого состояния. */
const DIFF_KEYS: Record<DiffState, string> = {
  same: 'analytics.diff.same',
  changed: 'analytics.diff.changed',
  noData: 'analytics.diff.noData',
}

function diffLabel(a: string | null, b: string | null): DiffState {
  if (!a || !b) return 'noData'
  return a === b ? 'same' : 'changed'
}

function ChangeRow({ label, from, to, state: given }: {
  label: string
  from: string | null
  to: string | null
  /**
   * Готовое состояние различия, когда сравнивать надо НЕ отображаемые строки.
   *
   * У кодов ЕТСНГ и уровней иерархии сравнение идёт по каноническому виду: «11005» и
   * «011005» — один груз, а как строки они различаются. В историческом корпусе таких
   * ног 808 035 из 11 577 091, так что это не теоретическая оговорка.
   */
  state?: DiffState
}) {
  const t = useT()
  const state = given ?? diffLabel(from, to)
  const tone = state === 'changed'
    ? 'text-[var(--text-primary)]'
    : 'text-[var(--text-tertiary)]'
  return (
    <div className="grid grid-cols-[minmax(0,110px)_1fr_auto] items-baseline gap-2 border-b border-[var(--border-subtle)] py-1 last:border-b-0">
      <span className="text-[11px] text-[var(--text-tertiary)]">{label}</span>
      <span className="min-w-0 text-[12px] text-[var(--text-secondary)]">
        <span title={from ?? undefined}>{from || '—'}</span>
        <span className="mx-1 text-[var(--text-tertiary)]">→</span>
        <span title={to ?? undefined}>{to || '—'}</span>
      </span>
      <span className={`shrink-0 text-[10px] ${tone}`}>{t(DIFF_KEYS[state])}</span>
    </div>
  )
}

function ChangesBlock({ p }: { p: HistoryPairDetail }) {
  const t = useT()
  return (
    <Block title={t('analytics.changes.title')}>
      <ChangeRow label={t('common.field.cargo')} from={p.import.cargoName} to={p.export.cargoName} />
      {/* Состояние различия кода — по каноническому виду, а печатается код как есть.
          «Изменилось» здесь означает «в импортной записи один код, в экспортной другой»,
          и ничего больше: связка сопоставлена аналитически, а не прослежена физически. */}
      <ChangeRow
        label={t('analytics.leg.etsng')}
        from={p.import.etsng}
        to={p.export.etsng}
        state={etsngCodesEqual(p.import.etsng, p.export.etsng)}
      />
      {/* Два уровня над кодом. Это справка о классификации, а не вывод о нарушении:
          «изменилось» здесь — такой же факт данных, как смена вагона ниже, и ни в
          одну причину, признак или балл это значение не уходит. */}
      {etsngComparisonRows(p.import.etsng, p.export.etsng).map((r) => (
        <ChangeRow key={r.key} label={r.label} from={r.from} to={r.to} state={r.state} />
      ))}
      <ChangeRow label={t('analytics.leg.gng')} from={p.import.gng} to={p.export.gng} />
      <ChangeRow
        label={t('analytics.leg.payer')}
        from={[p.import.payerName, p.import.payerCode].filter(Boolean).join(' · ') || null}
        to={[p.export.payerName, p.export.payerCode].filter(Boolean).join(' · ') || null}
      />
      <ChangeRow label={t('analytics.leg.mass')} from={weight(p.import.weightTons)} to={weight(p.export.weightTons)} />
      <ChangeRow label={t('analytics.hist.wagonOrContainer')} from={p.import.unit} to={p.export.unit} />
      <p className="mt-1.5 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
        {t('analytics.hist.legDifferenceNote')}
      </p>
    </Block>
  )
}

/* ── почему показана ─────────────────────────────────────────────────────────── */

function ContextBlock({ ctx }: { ctx: HistoryDetailContext }) {
  const t = useT()
  return (
    <Block title={t('analytics.ctx.title')}>
      <Row k={t('analytics.ctx.source')} v={ctx.title} />
      <Row k={t('analytics.ctx.selection')} v={ctx.selectionDescription} />
      {ctx.variantLabel && <Row k={t('analytics.ctx.variantGroup')} v={ctx.variantLabel} mono />}
    </Block>
  )
}

/* ── технические сведения ────────────────────────────────────────────────────── */

function TechnicalBlock({ p, ctx }: { p: HistoryPairDetail; ctx: HistoryDetailContext }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  return (
    <section data-block="history-detail-technical" className="rounded-[3px] border border-[var(--border-subtle)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
      >
        {open ? '▾' : '▸'} Технические сведения
      </button>
      {open && (
        <dl className="border-t border-[var(--border-subtle)] px-3 py-2">
          <Row k={t('analytics.tech.pairId')} v={p.identity.pairId} mono />
          <Row k={t('analytics.tech.importRecord')} v={p.identity.importSourceRecordId} mono />
          <Row k={t('analytics.tech.exportRecord')} v={p.identity.exportSourceRecordId} mono />
          <Row k={t('analytics.tech.corpus')} v={p.identity.corpus} />
          <Row k={t('analytics.tech.legFieldSource')} v={p.identity.legSource} />
          <Row k={t('analytics.tech.evidenceSet')} v={ctx.datasetType} mono />
          <Row k={t('analytics.tech.candidateFamily')} v={p.candidateFamily} mono />
          <Row k={t('analytics.tech.unitType')} v={p.physicalUnitType} mono />
        </dl>
      )}
    </section>
  )
}

/* ── карточка ────────────────────────────────────────────────────────────────── */

export function HistoricalTransportDetail({
  identity,
  context,
  onClose,
}: {
  identity: HistoryRowIdentity
  context: HistoryDetailContext
  onClose: () => void
}) {
  const t = useT()
  const { data, isLoading, isError } = useCaseHistoryDetailV3(
    identity.importRecordId, identity.exportRecordId)

  // Escape закрывает только эту карточку: обработчик карточки кейса под ней
  // получит событие лишь после того, как этот слой снимется.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  const p = data?.status === 'ok' ? data.pair : null
  const shipments = [identity.importShipment, identity.exportShipment].filter(Boolean).join(' → ')

  return (
    <div
      data-block="history-detail"
      role="dialog"
      aria-modal="true"
      aria-label={t('analytics.hist.title')}
      className="fixed inset-0 z-[60] flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-[720px] flex-col overflow-y-auto border-l border-[var(--border-default)] bg-[var(--bg-canvas)]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">
              {t('analytics.hist.detailTitle')}
            </h3>
            <p className="mt-0.5 truncate font-mono text-[11px] text-[var(--text-tertiary)]">
              {shipments || '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('analytics.hist.closeAria')}
            className="shrink-0 rounded-[3px] px-2 py-1 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
          >
            ✕
          </button>
        </header>

        {isLoading && (
          <div data-state="loading" className="space-y-2 p-4">
            <p className="text-[11px] text-[var(--text-tertiary)]">{t('analytics.hist.loading')}</p>
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-[3px] bg-[var(--bg-subtle)]" />
            ))}
          </div>
        )}

        {!isLoading && (isError || !p) && (
          <div data-state="error" className="p-4">
            <p className="text-[12px] leading-relaxed text-[var(--text-secondary)]">
              {t('analytics.hist.detailLoadError')}
            </p>
            <p className="mt-1 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
              {data?.status === 'not_found'
                ? t('analytics.hist.notFound')
                : data?.status === 'ambiguous'
                  ? t('analytics.hist.ambiguous')
                  : t('analytics.hist.unavailable')}
            </p>
          </div>
        )}

        {p && (
          <div data-state="ready" className="space-y-3 p-4">
            {/* Шапка: то, что относится к связке целиком. */}
            <section className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-3">
              <dl className="grid grid-cols-2 gap-x-4">
                <Row k={t('analytics.details.importDate')} v={ru(p.import.date)} mono />
                <Row k={t('analytics.details.exportDate')} v={ru(p.export.date)} mono />
                <Row k={t('analytics.hist.wagonOrContainer')} v={p.import.unit ?? p.export.unit} mono />
                <Row k={t('analytics.hist.interval')} v={days(p.daysBetween, t)} />
                <Row k={t('analytics.hist.importShipment')} v={p.import.shipmentId} mono />
                <Row k={t('analytics.hist.exportShipment')} v={p.export.shipmentId} mono />
              </dl>
            </section>

            <ContextBlock ctx={context} />

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {/* Две СТОРОНЫ связки. Подписи называют их записями, а не перевозками:
                  карточка показывает ровно две строки корпуса, соединённые в пару. */}
              <LegSection leg={p.import} title={t('analytics.hist.importLeg')} />
              <LegSection leg={p.export} title={t('analytics.hist.exportLeg')} />
            </div>

            <RouteBlock p={p} />
            <HandoverBlock p={p} />
            <ChangesBlock p={p} />

            {(!p.import.enriched || !p.export.enriched) && (
              <p className="text-[10px] leading-relaxed text-[var(--text-tertiary)]">
                {t('analytics.hist.partialFields')}
              </p>
            )}

            <TechnicalBlock p={p} ctx={context} />
          </div>
        )}
      </div>
    </div>
  )
}
